import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  CartList,
  CheckoutInfoBox,
  Button,
  ButtonType,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
} from '@amzn/burberry-ui-components';
import type { CartListItem as BaseCartListItem } from '@amzn/burberry-ui-components';
type CartListItem = BaseCartListItem & {
  isUnavailable?: boolean;
  availabilityMessage?: string;
};
import { awsRum } from 'src/rum';
import { AppDispatch } from 'src/store/store';
import { getStage } from 'src/utils/roadieHelper';
import { selectCart, setCart } from 'src/store/cartSlice';
import {
  hydrateCatalog,
  selectCatalog,
  selectHydrateCatalogComplete,
  selectCatalogError,
} from 'src/store/catalogSlice';
import {
  selectCsrfToken,
  selectClientId,
  selectExpiryTime,
  selectAuthState,
  selectValidateTokenComplete,
  selectValidateTokenInProgress,
  selectGetClientIdError
} from 'src/store/authSlice';
import { getClientId, getValidateToken } from 'src/store/authSlice';
import { selectTemplate, selectUseTransparentImages } from 'src/store/templateSlice';
import { Paths } from 'src/paths/Paths';
import { metricEmitter } from 'src/utils/metricEmitter';
import { handleAuth, reloadWindowOnStorageChange } from 'src/utils/utils';
import { getRedirectUrlForCurrentStage } from 'src/constants/lwa';
import {
  buildProductUrl,
  findCatalogItemUsingPurchaseItem,
  getPriceNumberFromString,
  isCatalogItemUnavailable,
  formatAvailabilityString,
  parseDeliveryMessage,
  getVariationValues,
  hydrateCatalogFromCart,
  getValidCart,
  getCartCheckoutMessage,
  MAX_ASINS_IN_CART,
  UH_OH,
  GENERIC_ERROR_STRING,
  YOUR_CART_STRING,
} from '@amzn/d2c-application-website-common';
import { resolveImageUrl } from 'src/utils/imageUtil';
import './Cart.scss';

const Cart: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const csrfToken = useSelector(selectCsrfToken);
  const clientId = useSelector(selectClientId);
  const getClientIdError = useSelector(selectGetClientIdError);
  const expiryTime = useSelector(selectExpiryTime);
  const authState = useSelector(selectAuthState);
  const accessTokenExists = useSelector(selectValidateTokenComplete);
  const validateTokenInProgress = useSelector(selectValidateTokenInProgress);
  const template = useSelector(selectTemplate);
  const useTransparentImages = useSelector(selectUseTransparentImages);
  const isLoggedIn = !!accessTokenExists && !!expiryTime && Date.now() < expiryTime;

  // Update document title
  useEffect(() => {
    document.title = template?.title ? `${YOUR_CART_STRING} - ${template.title}` : YOUR_CART_STRING;
  }, [template]);

  // Validate auth token on cart load — fires once on mount
  useEffect(() => {
    dispatch(getValidateToken({ stage: getStage(), csrfToken }));
  }, []);

  // Reload page when LWA popup stores auth state
  useEffect(() => {
    return reloadWindowOnStorageChange(dispatch, authState);
  }, [dispatch, authState]);

  const [pageLoadTime] = useState(Date.now());

  const isNotEmpty = cart?.items && cart.items.length > 0;

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: 'Cart' });
  }, []);

  // Fetch client ID for LWA
  useEffect(() => {
    if (!clientId) {
      dispatch(getClientId({ stage: getStage() }));
    }
  }, [dispatch, clientId]);

  const [catalogRequested, setCatalogRequested] = useState(false);
  const [pageLoadRecorded, setPageLoadRecorded] = useState(false);

  // Hydrate catalog from cart items on mount or auth state change
  useEffect(() => {
    if (isNotEmpty && !validateTokenInProgress) {
      setCatalogRequested(true);
      hydrateCatalogFromCart(cart, dispatch, hydrateCatalog, getStage(), csrfToken);
    }
  }, [expiryTime, accessTokenExists, csrfToken, validateTokenInProgress]);

  // Page load metric — only fires after a fresh catalog load triggered by this page
  useEffect(() => {
    if (pageLoadRecorded) return;
    if (!isNotEmpty || (catalogRequested && hydrateCatalogComplete)) {
      const loadTime = Date.now() - pageLoadTime;
      awsRum?.recordEvent('PageLoad', { page: 'Cart', loadTimeMs: String(loadTime) });
      setPageLoadRecorded(true);
    }
  }, [hydrateCatalogComplete, isNotEmpty, pageLoadTime, pageLoadRecorded, catalogRequested]);

  // Hydrate cart items using getValidCart and map to UI shape
  const validCart = getValidCart(cart?.items || [], catalog || []);

  const cartListItems: CartListItem[] = validCart.cartItems.map((item) => {
    const unavailable = isCatalogItemUnavailable(item.availability);
    const { src: image, fallback: imageFallback } = resolveImageUrl(item.image, useTransparentImages);
    const catalogEntry = findCatalogItemUsingPurchaseItem(catalog || [], { asin: item.identifier?.itemAsin, customId: item.identifier?.customId, customizationToken: item.identifier?.customizationToken });
    return {
      id: [item.identifier?.itemAsin, item.identifier?.customId, item.identifier?.customizationToken].filter(Boolean).join('-') || '',
      image,
      imageFallback,
      title: item.title || '',
      price: item.price || '',
      quantity: item.quantity || 0,
      minQuantity: item.availability?.minOrderQuantity ?? 1,
      maxQuantity: item.availability?.maxOrderQuantity ?? 99,
      variations: Object.entries(
        getVariationValues(item.variations, item.identifier?.itemAsin, item.identifier?.customId, item.identifier?.customizationToken)
      ).map(([key, value]) => ({ key, value })),
      deliveryMessage: unavailable ? undefined : parseDeliveryMessage(item.deliveryMessage),
      isUnavailable: unavailable,
      availabilityMessage: unavailable
        ? formatAvailabilityString(item.availability?.primaryMessage, item.availability?.secondaryMessage)
        : undefined,
      primeBadgeData: catalogEntry?.primeBadgeData,
    };
  });

  const handleRemoveItem = (index: number) => {
    const item = cart?.items?.[index];
    if (!item || !cart) return;
    awsRum?.recordEvent('CartRemoveItem', { asin: item.asin, index: String(index) });
    const updatedItems = cart.items.filter((_: any, i: number) => i !== index);
    const newSubtotal = updatedItems.reduce((sum: number, it: any) => {
      const catalogEntry = findCatalogItemUsingPurchaseItem(catalog || [], { asin: it.asin });
      const price = getPriceNumberFromString(catalogEntry?.price || '0');
      return sum + price * it.quantity;
    }, 0);
    dispatch(setCart({ cart: { items: updatedItems, quantity: updatedItems.reduce((s: number, it: any) => s + it.quantity, 0), subtotal: newSubtotal } }));
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const item = cart?.items?.[index];
    if (!item || !cart) return;
    awsRum?.recordEvent('CartQuantityChange', { asin: item.asin, quantity: String(quantity) });
    const updatedItems = cart.items.map((it: any, i: number) =>
      i === index ? { ...it, quantity } : it,
    );
    const newSubtotal = updatedItems.reduce((sum: number, it: any) => {
      const catalogItem = findCatalogItemUsingPurchaseItem(catalog || [], { asin: it.asin });
      const price = getPriceNumberFromString(catalogItem?.price || '0');
      return sum + price * it.quantity;
    }, 0);
    dispatch(setCart({ cart: { items: updatedItems, quantity: updatedItems.reduce((s: number, it: any) => s + it.quantity, 0), subtotal: newSubtotal } }));
  };

  const handleItemClick = (index: number) => {
    const item = cart?.items?.[index];
    if (!item) return;
    awsRum?.recordEvent('CartItemClick', { asin: item.asin });
    navigate(buildProductUrl(item.asin, item.customId, item.customizationToken));
  };

  const handleContinueShopping = () => {
    awsRum?.recordEvent('ButtonClick', { action: 'ContinueShopping' });
    navigate(Paths.home);
  };

  const handleCheckout = () => {
    awsRum?.recordEvent('ButtonClick', { action: 'Checkout' });
    if (isLoggedIn) {
      navigate(Paths.previewCheckout);
    } else {
      handleAuth(clientId || '', getRedirectUrlForCurrentStage());
    }
  };

  const subtotal = validCart.subtotal;
  const hasUnavailableItems = cartListItems.some((item) => item.isUnavailable);
  const maxItemsExceeded = cartListItems.length > MAX_ASINS_IN_CART;
  const checkoutMessage = getCartCheckoutMessage(isLoggedIn, maxItemsExceeded, hasUnavailableItems);
  const checkoutDisabled = hasUnavailableItems || maxItemsExceeded;

  // While logged out, hold the LOGIN_WITH_AMAZON button back until clientId has loaded (or failed) —
  // otherwise a click before getClientId resolves opens the LWA popup with a blank client_id.
  const clientIdPending = !isLoggedIn && !clientId && !getClientIdError;

  // Loading state — show when catalog hasn't finished hydrating, when error occurred during token
  // validation (retry in-flight), or when clientId is still pending for a logged-out cart.
  if ((!hydrateCatalogComplete && isNotEmpty) || (catalogError && validateTokenInProgress) || (isNotEmpty && clientIdPending)) {
    return (
      <div className="cartPage cartPage--loading" data-testid="cartPage">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  return (
    <div className="cartPage" data-testid="cartPage">
      {catalogError && !validateTokenInProgress && (
        <div className="cartPage__error" data-testid="cartError">
          <ErrorBox
            heading={UH_OH}
            message={GENERIC_ERROR_STRING}
            metricEmitter={metricEmitter}
          />
        </div>
      )}
      {!catalogError && (
        <>
      <div className="cartPage__list">
        <CartList
          items={cartListItems}
          onRemoveItem={handleRemoveItem}
          onQuantityChange={handleQuantityChange}
          onItemClick={handleItemClick}
          metricEmitter={metricEmitter}
        />
      </div>

      <div className="cartPage__sidebar">
        {cartListItems.length === 0 ? (
          <Button
            type={ButtonType.CONTINUE_SHOPPING}
            onClick={handleContinueShopping}
            metricEmitter={metricEmitter}
          />
        ) : isLoggedIn ? (
          <>
            <CheckoutInfoBox
              subtotalAmount={`$${subtotal}`}
              {...(checkoutDisabled && { warningMessage: checkoutMessage })}
              cardTitle=""
              cardDescription=""
            />
            <Button
              type={ButtonType.BUY_WITH_AMAZON}
              onClick={handleCheckout}
              metricEmitter={metricEmitter}
              disabled={checkoutDisabled}
            />
          </>
        ) : (
          <CheckoutInfoBox
            subtotalAmount={`$${subtotal}`}
            {...(checkoutDisabled && { warningMessage: checkoutMessage })}
          >
            <Button
              type={ButtonType.LOGIN_WITH_AMAZON}
              onClick={handleCheckout}
              metricEmitter={metricEmitter}
              disabled={checkoutDisabled}
            />
          </CheckoutInfoBox>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Cart;
